/**
 * HUD - In-game heads-up display
 *
 * Features:
 * - Soldier count (top-left with icon and pill background)
 * - Score display (top-right with gold color and drop shadow)
 * - Weapon indicator (bottom of screen above army)
 * - Score increment animation (scale + color flash)
 * - Top gradient bar for visual polish
 *
 * Per PRD section 5.2 and Task #18 specifications
 */

import Phaser from 'phaser';
import {
  COLORS,
  FONTS,
  GAME_WIDTH,
  SAFE_AREA,
  DURATIONS,
  UI_SIZES,
} from '../config';
import { WeaponType } from '../utils/constants';
import { getHudBounds } from '../utils/responsive';
import { fitImageContain, getWeaponGunTextureKey } from './weaponVisuals';

export interface HUDConfig {
  /** Initial soldier count */
  soldierCount?: number;
  /** Initial score */
  score?: number;
  /** Initial weapon type */
  weapon?: WeaponType;
  /** Initial base HP */
  baseHp?: number;
  /** Current campaign level */
  level?: number;
}

/**
 * HUD displays game state information during gameplay
 *
 * Usage:
 *   const hud = new HUD(scene);
 *   hud.updateSoldierCount(25);
 *   hud.updateScore(1500);
 *   hud.updateWeapon(WeaponType.Rifle);
 */
export class HUD extends Phaser.GameObjects.Container {
  private static readonly SIDE_MARGIN = 10;
  private static readonly TOP_PAD = 12;
  private static readonly CARD_HEIGHT = 44;
  private static readonly CARD_RADIUS = 10;
  private static readonly CARD_INNER_PAD_X = 10;
  private static readonly VALUE_BASE_Y = 27;
  private static readonly LABEL_BASE_Y = 11;
  private static readonly CENTER_GAP = 54;
  private static readonly PAUSE_RESERVE = UI_SIZES.iconButton + 24;
  private static readonly WEAPON_SLOT_SIZE = 40;

  // Soldier count elements
  private soldierCountBg!: Phaser.GameObjects.Graphics;
  private soldierLabel!: Phaser.GameObjects.Text;
  private soldierValue!: Phaser.GameObjects.Text;
  private currentSoldierCount: number = 0;
  private soldierCountTween: Phaser.Tweens.Tween | null = null;

  // Score elements
  private scoreBg!: Phaser.GameObjects.Graphics;
  private scoreLabel!: Phaser.GameObjects.Text;
  private scoreValue!: Phaser.GameObjects.Text;
  private currentScore: number = 0;
  private scoreAnimating: boolean = false;

  // Base HP elements
  private baseLabel!: Phaser.GameObjects.Text;
  private baseValue!: Phaser.GameObjects.Text;
  private currentBaseHp: number = 12;

  // Mission progress rail
  private progressTrack!: Phaser.GameObjects.Graphics;
  private progressFill!: Phaser.GameObjects.Graphics;
  private progressLabel!: Phaser.GameObjects.Text;
  private currentProgress: number = 0;
  private currentLevel: number = 1;

  // Weapon icon
  private weaponBg!: Phaser.GameObjects.Graphics;
  private weaponIcon!: Phaser.GameObjects.Image;
  private weaponTween: Phaser.Tweens.Tween | null = null;
  private currentWeapon: WeaponType = WeaponType.Pistol;
  private isDestroyed: boolean = false;

  // Top gradient
  private topGradient!: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, config: HUDConfig = {}) {
    super(scene, 0, 0);

    this.currentSoldierCount = config.soldierCount ?? 10;
    this.currentScore = config.score ?? 0;
    this.currentWeapon = config.weapon ?? WeaponType.Pistol;
    this.currentBaseHp = config.baseHp ?? 12;
    this.currentLevel = config.level ?? 1;

    this.createTopGradient();
    this.createSoldierCounter();
    this.createScoreDisplay();
    this.createBaseDisplay();
    this.createProgressDisplay();
    this.createWeaponIndicator();
    this.layout();

    // Set high depth to render above game objects
    this.setDepth(50);

    scene.add.existing(this);
    scene.scale.on('resize', this.layout, this);
  }

  // ===========================================================================
  // CREATION
  // ===========================================================================

  /**
   * Creates the top gradient bar for visual polish
   */
  private createTopGradient(): void {
    this.topGradient = this.scene.add.graphics();

    const gradientHeight = SAFE_AREA.top + 50;

    // Create gradient from overlay color to transparent
    for (let y = 0; y < gradientHeight; y++) {
      const alpha = 1 - (y / gradientHeight);
      this.topGradient.fillStyle(COLORS.bgOverlay, alpha * 0.6);
      this.topGradient.fillRect(0, y, GAME_WIDTH, 1);
    }

    this.add(this.topGradient);
  }

  /**
   * Creates the soldier count display (top-left)
   */
  private createSoldierCounter(): void {
    this.soldierCountBg = this.scene.add.graphics();
    this.add(this.soldierCountBg);

    this.soldierLabel = this.scene.add.text(0, 0, 'ARMY', {
      fontFamily: FONTS.body,
      fontSize: '11px',
      color: '#DCE5F2',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.add(this.soldierLabel);

    this.soldierValue = this.scene.add.text(0, 0, this.formatNumber(this.currentSoldierCount), {
      fontFamily: FONTS.display,
      fontSize: '20px',
      color: '#FFFFFF',
      fontStyle: 'bold',
    });
    this.soldierValue.setOrigin(0, 0.5);
    this.add(this.soldierValue);
  }

  /**
   * Updates the pill background to fit the number
   */
  private updateSoldierCountBackground(x: number, y: number, width: number): void {
    this.soldierCountBg.clear();
    this.soldierCountBg.fillStyle(COLORS.bgOverlay, 0.78);
    this.soldierCountBg.fillRoundedRect(x, y, width, HUD.CARD_HEIGHT, HUD.CARD_RADIUS);
    this.soldierCountBg.lineStyle(1, COLORS.player, 0.42);
    this.soldierCountBg.strokeRoundedRect(x, y, width, HUD.CARD_HEIGHT, HUD.CARD_RADIUS);
  }

  /**
   * Creates the score display (top-right, left of pause button)
   */
  private createScoreDisplay(): void {
    this.scoreBg = this.scene.add.graphics();
    this.add(this.scoreBg);

    this.scoreLabel = this.scene.add.text(0, 0, 'SCORE', {
      fontFamily: FONTS.body,
      fontSize: '11px',
      color: '#DCE5F2',
      fontStyle: 'bold',
    });
    this.scoreLabel.setOrigin(1, 0.5);
    this.add(this.scoreLabel);

    this.scoreValue = this.scene.add.text(0, 0, this.formatNumber(this.currentScore), {
      fontFamily: FONTS.display,
      fontSize: '20px',
      color: '#FFFFFF',
      fontStyle: 'bold',
    });
    this.scoreValue.setOrigin(1, 0.5);
    this.add(this.scoreValue);
  }

  private createWeaponIndicator(): void {
    this.weaponBg = this.scene.add.graphics();
    this.add(this.weaponBg);

    this.weaponIcon = this.scene.add.image(0, 0, getWeaponGunTextureKey(this.currentWeapon)).setOrigin(0.5);
    this.add(this.weaponIcon);
  }

  private createBaseDisplay(): void {
    this.baseLabel = this.scene.add.text(0, 0, 'SHIELD', {
      fontFamily: FONTS.body,
      fontSize: '10px',
      color: '#DCE5F2',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0.5);
    this.add(this.baseLabel);

    this.baseValue = this.scene.add.text(0, 0, `${this.currentBaseHp}`, {
      fontFamily: FONTS.display,
      fontSize: '18px',
      color: '#FFFFFF',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0.5);
    this.add(this.baseValue);
  }

  private createProgressDisplay(): void {
    this.progressTrack = this.scene.add.graphics();
    this.progressFill = this.scene.add.graphics();
    this.progressLabel = this.scene.add.text(0, 0, `L${String(this.currentLevel).padStart(2, '0')}`, {
      fontFamily: FONTS.numbers,
      fontSize: '10px',
      color: '#73F7FF',
      letterSpacing: 1,
    }).setOrigin(0.5);
    this.add([this.progressTrack, this.progressFill, this.progressLabel]);
  }

  private layout(): void {
    const hudBounds = getHudBounds(58);
    const cardTop = Math.max(SAFE_AREA.top + 8, hudBounds.top + HUD.TOP_PAD);
    const leftCardX = HUD.SIDE_MARGIN;

    const scoreRightLimit = GAME_WIDTH - HUD.PAUSE_RESERVE;
    const centerLeftLimit = GAME_WIDTH / 2 - HUD.CENTER_GAP / 2;
    const centerRightLimit = GAME_WIDTH / 2 + HUD.CENTER_GAP / 2;

    const leftTextWidth = Math.ceil(this.soldierValue.width);
    const leftRawWidth = leftTextWidth + HUD.CARD_INNER_PAD_X * 2;
    const leftMaxWidth = Math.max(96, centerLeftLimit - leftCardX);
    const leftCardWidth = Phaser.Math.Clamp(leftRawWidth, 96, leftMaxWidth);

    this.updateSoldierCountBackground(leftCardX, cardTop, leftCardWidth);
    const leftTextX = Math.round(leftCardX + HUD.CARD_INNER_PAD_X);
    this.soldierLabel.setPosition(leftTextX, Math.round(cardTop + HUD.LABEL_BASE_Y));
    this.soldierValue.setPosition(leftTextX, Math.round(cardTop + HUD.VALUE_BASE_Y));

    const scoreTextWidth = Math.ceil(this.scoreValue.width);
    const scoreRawWidth = scoreTextWidth + HUD.CARD_INNER_PAD_X * 2;
    const scoreMaxWidth = Math.max(96, scoreRightLimit - centerRightLimit);
    const scoreCardWidth = Phaser.Math.Clamp(scoreRawWidth, 96, scoreMaxWidth);
    const scoreCardX = scoreRightLimit - scoreCardWidth;

    this.scoreBg.clear();
    this.scoreBg.fillStyle(COLORS.bgOverlay, 0.78);
    this.scoreBg.fillRoundedRect(scoreCardX, cardTop, scoreCardWidth, HUD.CARD_HEIGHT, HUD.CARD_RADIUS);
    this.scoreBg.lineStyle(1, 0x00D4FF, 0.38);
    this.scoreBg.strokeRoundedRect(scoreCardX, cardTop, scoreCardWidth, HUD.CARD_HEIGHT, HUD.CARD_RADIUS);

    const textRight = Math.round(scoreCardX + scoreCardWidth - HUD.CARD_INNER_PAD_X);
    this.scoreLabel.setPosition(textRight, Math.round(cardTop + HUD.LABEL_BASE_Y));
    this.scoreValue.setPosition(textRight, Math.round(cardTop + HUD.VALUE_BASE_Y));

    const slotSize = HUD.WEAPON_SLOT_SIZE;
    const slotX = Math.round(GAME_WIDTH / 2 - slotSize / 2);
    const slotY = Math.round(cardTop + (HUD.CARD_HEIGHT - slotSize) / 2);
    this.weaponBg.clear();
    this.weaponBg.fillStyle(COLORS.bgOverlay, 0.78);
    this.weaponBg.fillRoundedRect(slotX, slotY, slotSize, slotSize, 10);
    this.weaponBg.lineStyle(1, COLORS.gold, 0.42);
    this.weaponBg.strokeRoundedRect(slotX, slotY, slotSize, slotSize, 10);

    this.weaponIcon.setPosition(slotX + slotSize / 2, slotY + slotSize / 2);
    fitImageContain(this.weaponIcon, slotSize - 9, slotSize - 17);

    const railY = Math.round(cardTop + HUD.CARD_HEIGHT + 16);
    const railX = 94;
    const railWidth = GAME_WIDTH - railX - HUD.PAUSE_RESERVE + 18;
    this.progressTrack.clear();
    this.progressTrack.fillStyle(0x02070D, 0.92);
    this.progressTrack.fillRoundedRect(railX, railY - 3, railWidth, 6, 3);
    this.progressTrack.lineStyle(1, 0x31566C, 0.72);
    this.progressTrack.strokeRoundedRect(railX, railY - 3, railWidth, 6, 3);
    this.progressFill.clear();
    this.progressFill.fillStyle(0x00D4FF, 0.95);
    this.progressFill.fillRoundedRect(
      railX + 1,
      railY - 2,
      Math.max(2, (railWidth - 2) * this.currentProgress),
      4,
      2
    );
    this.progressLabel.setPosition(railX + railWidth / 2, railY - 13);

    this.baseLabel.setOrigin(0, 0.5).setPosition(HUD.SIDE_MARGIN, railY - 8);
    this.baseValue.setOrigin(0, 0.5).setPosition(HUD.SIDE_MARGIN, railY + 7);

    const gradientHeight = railY + 22;
    this.topGradient.clear();
    for (let y = 0; y < gradientHeight; y++) {
      const alpha = 1 - (y / gradientHeight);
      this.topGradient.fillStyle(COLORS.bgOverlay, alpha * 0.6);
      this.topGradient.fillRect(0, y, GAME_WIDTH, 1);
    }
  }

  // ===========================================================================
  // UPDATES
  // ===========================================================================

  /**
   * Updates the soldier count display
   */
  updateSoldierCount(count: number): void {
    const previousCount = this.currentSoldierCount;
    this.currentSoldierCount = Math.max(0, Math.round(count));

    this.soldierValue.setText(this.formatNumber(this.currentSoldierCount));
    this.layout();

    if (this.currentSoldierCount !== previousCount) {
      this.playSoldierCountPulse(this.currentSoldierCount > previousCount);
    }

    // Flash animation when count changes significantly
    if (this.currentSoldierCount > previousCount + 5) {
      // Gained soldiers - green flash
      this.flashSoldierCount(COLORS.gate);
    } else if (this.currentSoldierCount < previousCount) {
      // Lost soldiers - red flash
      this.flashSoldierCount(COLORS.enemy);
    }
  }

  /**
   * Flashes the soldier count with a color
   */
  private flashSoldierCount(color: number): void {
    this.soldierValue.setColor(`#${color.toString(16).padStart(6, '0')}`);

    this.scene.time.delayedCall(150, () => {
      this.soldierValue.setColor('#FFFFFF');
    });
  }

  /**
   * Small pulse animation to make count changes feel snappy
   */
  private playSoldierCountPulse(isGain: boolean): void {
    if (this.soldierCountTween) {
      this.soldierCountTween.stop();
      this.soldierCountTween = null;
    }

    const scale = isGain ? 1.15 : 0.9;
    const targets = [this.soldierValue];

    this.soldierCountTween = this.scene.tweens.add({
      targets,
      scaleX: scale,
      scaleY: scale,
      duration: 80,
      ease: 'Back.easeOut',
      yoyo: true,
      onComplete: () => {
        targets.forEach(target => target.setScale(1));
        this.soldierCountTween = null;
      },
    });
  }

  /**
   * Updates the score display with animation
   */
  updateScore(score: number): void {
    const roundedScore = Math.max(0, Math.round(score));
    if (roundedScore === this.currentScore) return;

    const previousScore = this.currentScore;
    this.currentScore = roundedScore;

    // Animate the score increment
    this.animateScoreIncrement(previousScore, roundedScore);
  }

  updateBaseHp(baseHp: number): void {
    const clamped = Math.max(0, Math.round(baseHp));
    if (clamped === this.currentBaseHp) return;
    this.currentBaseHp = clamped;
    this.baseValue.setText(`${clamped}`);
    this.baseValue.setColor(clamped <= 4 ? '#FF6B6B' : '#FFFFFF');
    this.layout();
  }

  updateProgress(progress01: number): void {
    const clamped = Phaser.Math.Clamp(progress01, 0, 1);
    if (Math.abs(clamped - this.currentProgress) < 0.002) return;
    this.currentProgress = clamped;
    this.layout();
  }

  getBaseHp(): number {
    return this.currentBaseHp;
  }

  /**
   * Animates score counting up with visual effects
   */
  private animateScoreIncrement(from: number, to: number): void {
    // If already animating, just continue (next call will get the latest value)
    if (this.scoreAnimating) {
      return;
    }

    this.scoreAnimating = true;

    // Scale up animation
    this.scene.tweens.add({
      targets: this.scoreValue,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: DURATIONS.scoreIncrement / 2,
      ease: 'Back.easeOut',
      yoyo: true,
    });

    // Count up animation
    const duration = Math.min(DURATIONS.scoreIncrement, Math.abs(to - from) * 5);
    this.scene.tweens.addCounter({
      from: from,
      to: to,
      duration: duration,
      ease: 'Cubic.easeOut',
      onUpdate: (tween) => {
        const tweenValue = tween.getValue();
        const value = Math.floor(tweenValue ?? to);
        this.scoreValue.setText(this.formatNumber(value));
        this.layout();
      },
      onComplete: () => {
        this.scoreValue.setText(this.formatNumber(to));
        this.layout();
        this.scoreAnimating = false;
      },
    });
  }

  /**
   * Updates the weapon indicator with upgrade animation
   */
  updateWeapon(weapon: WeaponType): void {
    if (weapon === this.currentWeapon) return;
    this.currentWeapon = weapon;
    this.weaponIcon.setTexture(getWeaponGunTextureKey(weapon));
    this.layout();

    if (this.weaponTween) {
      this.weaponTween.stop();
      this.weaponTween = null;
    }
    this.weaponTween = this.scene.tweens.add({
      targets: this.weaponIcon,
      scaleX: this.weaponIcon.scaleX * 1.14,
      scaleY: this.weaponIcon.scaleY * 1.14,
      duration: 95,
      ease: 'Back.easeOut',
      yoyo: true,
      onComplete: () => {
        this.layout();
        this.weaponTween = null;
      },
    });
  }

  // ===========================================================================
  // GETTERS
  // ===========================================================================

  /**
   * Gets the current displayed soldier count
   */
  getSoldierCount(): number {
    return this.currentSoldierCount;
  }

  /**
   * Gets the current score
   */
  getScore(): number {
    return this.currentScore;
  }

  /**
   * Gets the current weapon type
   */
  getWeapon(): WeaponType {
    return this.currentWeapon;
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  /**
   * Destroys the HUD and all its children
   */
  destroy(fromScene?: boolean): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    this.scene?.scale?.off('resize', this.layout, this);

    // Clean up any running tweens
    this.scene?.tweens?.killTweensOf(this.scoreValue);
    this.scene?.tweens?.killTweensOf(this.soldierValue);
    this.scene?.tweens?.killTweensOf(this.weaponIcon);
    this.soldierCountTween?.stop();
    this.soldierCountTween = null;
    this.weaponTween?.stop();
    this.weaponTween = null;

    super.destroy(fromScene);
  }

  private formatNumber(value: number): string {
    return Math.max(0, Math.round(value)).toLocaleString('en-US');
  }
}
